import { cn } from '@buakei/ui';
import { useEffect, type ReactNode } from 'react';

type PageProps = {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  width?: 'default' | 'wide';
  /** Fill the height below the app header, for a workspace that must fit without scrolling. */
  fill?: boolean;
  children: ReactNode;
};

/** Page frame: one h1 per page and a descriptive document title. */
export function Page({
  title,
  description,
  actions,
  width = 'default',
  fill = false,
  children,
}: PageProps) {
  useEffect(() => {
    document.title = `${title} · Buakei AI`;
  }, [title]);

  return (
    <div
      className={cn(
        'mx-auto flex flex-col',
        fill ? 'h-full min-h-0 gap-3 p-3 md:px-6 md:pt-4 md:pb-3' : 'gap-6 p-4 md:p-6 lg:p-8',
        width === 'wide' ? 'max-w-7xl' : 'max-w-5xl',
      )}
    >
      <header
        className={cn(
          'flex shrink-0 flex-col gap-3 md:flex-row md:items-end md:justify-between',
          fill ? 'pb-0' : 'pb-2',
        )}
      >
        <div className="flex flex-col gap-1">
          <h1 className="font-serif text-2xl font-semibold text-navy-900 md:text-3xl">{title}</h1>
          {description ? (
            <div className="max-w-2xl leading-relaxed text-muted-600">{description}</div>
          ) : null}
        </div>
        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
      </header>
      {children}
    </div>
  );
}
